import { ChangeDetectionStrategy, Component } from '@angular/core';
import { elapsedToString, TimeUnit } from '@sama/js-core';
import { combineLatest, Observable, of, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { DraftSubmissionService } from '../submissions/draft-submission.service';

@Component({
  selector: 'h3-objectless-last-saved-badge',
  templateUrl: './objectless-last-saved-badge.component.html',
  styleUrls: ['./objectless-last-saved-badge.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ObjectlessLastSavedBadgeComponent {
  lastSaveInfo$: Observable<{
    lastSave: string | null;
    failureCount: number;
    shouldDisplayFailureCount: boolean;
    shouldDisplayError: boolean;
  }>;

  constructor(
    store: TaskWorkspaceStore,
    draftSubmissionService: DraftSubmissionService,
  ) {
    const lastSave$ = store('lastSavedDraftTimestamp').$.pipe(
      // switchMap allows to unsubscribe from previous timer
      // and start a new timer from this timestamp
      switchMap((timestamp) => {
        // timestamp is deleted when task is submitted
        // we want to emit a falsy value to hide the badge
        if (!timestamp) {
          return of(null);
        }
        // we need to recalculate the elapsed time every minute or else
        // the badge will always display 'Last saved 0 min ago'
        return timer(0, 60_000).pipe(
          map(() => this.convertDraftTimestampToElapsedTime(timestamp)),
        );
      }),
    );

    this.lastSaveInfo$ = combineLatest([
      lastSave$,
      draftSubmissionService.consecutiveFailureCount$,
    ]).pipe(
      map(([lastSave, failureCount]) => ({
        lastSave,
        failureCount,
        shouldDisplayFailureCount: failureCount > 0 && failureCount < 11,
        shouldDisplayError: failureCount > 10,
      })),
    );
  }

  private convertDraftTimestampToElapsedTime(timestamp: number) {
    const elapsedTime = Date.now() - timestamp;
    const timeUnits = [TimeUnit.Minutes];

    // display hours starting from 61 minutes
    if (elapsedTime >= 3_660_000) {
      timeUnits.unshift(TimeUnit.Hours);
    }

    const split = elapsedToString(elapsedTime, timeUnits).split(' ');
    const minuteIndex = split.indexOf('m');
    split[minuteIndex] = +split[minuteIndex - 1] > 1 ? 'minutes' : 'minute';

    const hourIndex = split.indexOf('h');
    if (hourIndex > 0) {
      split[hourIndex] = +split[hourIndex - 1] > 1 ? 'hours' : 'hour';
    }

    return split.join(' ');
  }
}
